// src/pages/Admin/ProductPlans.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authenticatedFetch } from '../../utils/api';

const API_BASE = process.env.REACT_APP_API_URL;

function ProductPlans() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    premium: '',
    coverage: '',
    status: 'Active'
  });

  // Check admin auth
  useEffect(() => {
    const adminSession = localStorage.getItem('fsl_admin_session');
    if (!adminSession) {
      toast.error('Please log in as admin');
      navigate('/admin-login', { replace: true });
      return;
    }
    fetchPlans();
  }, [navigate]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      // Replace with your actual endpoint
      const response = await authenticatedFetch(`${API_BASE}/admin/product-plans`, 'GET');
      if (response?.status === 'success') {
        setPlans(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      // Sample data for demo
      setPlans([
        { id: 1, name: 'Seniors Basic', description: 'Basic health coverage for seniors', premium: 30000, coverage: 'Outpatient, Inpatient', status: 'Active' },
        { id: 2, name: 'Seniors Prime', description: 'Comprehensive coverage with wellness', premium: 50000, coverage: 'Full medical, Dental, Optical', status: 'Active' },
        { id: 3, name: 'FlexiPlan Standard', description: 'Flexible coverage for working adults', premium: 25000, coverage: 'Outpatient, Emergency', status: 'Inactive' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        name: plan.name,
        description: plan.description,
        premium: plan.premium,
        coverage: plan.coverage,
        status: plan.status
      });
    } else {
      setEditingPlan(null);
      setFormData({ name: '', description: '', premium: '', coverage: '', status: 'Active' });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPlan(null);
    setFormData({ name: '', description: '', premium: '', coverage: '', status: 'Active' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, premium: Number(formData.premium) };
      if (editingPlan) {
        // Update existing
        await authenticatedFetch(`${API_BASE}/admin/product-plans/${editingPlan.id}`, 'PUT', payload);
        toast.success('Plan updated successfully');
      } else {
        // Create new
        await authenticatedFetch(`${API_BASE}/admin/product-plans`, 'POST', payload);
        toast.success('Plan created successfully');
      }
      handleCloseModal();
      fetchPlans();
    } catch (error) {
      console.error('Save error:', error);
      toast.error(editingPlan ? 'Failed to update plan' : 'Failed to create plan');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete plan "${name}"?`)) return;
    try {
      await authenticatedFetch(`${API_BASE}/admin/product-plans/${id}`, 'DELETE');
      toast.success('Plan deleted');
      fetchPlans();
    } catch (error) {
      toast.error('Failed to delete the plan');
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';
      await authenticatedFetch(`${API_BASE}/admin/product-plans/${id}/status`, 'PATCH', { status: newStatus });
      toast.success(`Plan ${newStatus.toLowerCase()}`);
      fetchPlans();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const filteredPlans = plans.filter(plan =>
    plan.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plan.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleLogout = () => {
    localStorage.removeItem('fsl_admin_session');
    toast.success('Logged out');
    navigate('/admin-login', { replace: true });
  };

  return (
    <div className="admin-page">
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
      
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <Link to="/admin" className="brand-logo">
            <img src="/images/fsl_logo.png" alt="FSL Admin" style={{ height: '45px' }} />
            <span style={{ marginLeft: '8px', fontSize: '18px', fontWeight: '700', color: '#1f2937' }}>Admin</span>
          </Link>
        </div>
        <nav className="sidebar-nav">
          {[
            { section: 'MAIN', items: [{ name: 'Dashboard', icon: 'fa-chart-line', path: '/admin' }] },
            { section: 'MANAGEMENT', items: [
                { name: 'Users', icon: 'fa-users', path: '/admin/users' },
                { name: 'HMO Links', icon: 'fa-link', path: '/admin/links' },
                { name: 'Product Plans', icon: 'fa-file-medical', path: '/admin/product-plans', active: true },
                { name: 'Hospitals', icon: 'fa-hospital', path: '/admin/hospitals' },
                { name: 'Companies', icon: 'fa-building', path: '/admin/companies' },
                { name: 'Transactions', icon: 'fa-exchange-alt', path: '/admin/transactions' }
              ]},
            { section: 'SYSTEM', items: [
                { name: 'Settings', icon: 'fa-cog', path: '/admin/settings' },
                { name: 'Audit Logs', icon: 'fa-clipboard-list', path: '/admin/logs' }
              ]}
          ].map((section, idx) => (
            <div key={idx} className="nav-section">
              <h6 className="nav-section-title">{section.section}</h6>
              <ul className="nav-list">
                {section.items.map((item, itemIdx) => (
                  <li key={itemIdx}>
                    <Link to={item.path} className={`nav-link ${item.active ? 'active' : ''}`}>
                      <i className={`fas ${item.icon} me-2`}></i>{item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="nav-section">
            <h6 className="nav-section-title">ACCOUNT</h6>
            <ul className="nav-list">
              <li><Link to="/admin/profile" className="nav-link"><i className="fas fa-user me-2"></i>Profile</Link></li>
              <li><a href="/" className="nav-link" onClick={handleLogout}><i className="fas fa-sign-out-alt me-2"></i>Logout</a></li>
            </ul>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        <header className="top-header">
          <div className="header-left">
            <button className="sidebar-toggle d-lg-none" onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}>
              <i className="fas fa-bars"></i>
            </button>
            <div className="page-title">
              <h4 className="mb-0" style={{ color: '#1f2937', fontWeight: '600' }}>Product Plans</h4>
            </div>
          </div>
          <div className="header-right">
            <button className="header-icon"><i className="fas fa-search"></i></button>
            <button className="header-icon"><i className="fas fa-bell"></i><span className="badge">2</span></button>
            <div className="user-avatar" style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg, #F89423 0%, #F89423 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '600', cursor: 'pointer' }}>AD</div>
          </div>
        </header>

        <div className="content-wrapper" style={{ padding: '30px' }}>
          {/* Header Actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <div>
              <h5 className="mb-1">Manage Product Plans</h5>
              <p className="text-muted small mb-0">{filteredPlans.length} plans found</p>
            </div>
            <button className="btn" onClick={() => handleOpenModal()} style={{ backgroundColor: '#F89423', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <i className="fas fa-plus"></i>Add Plan
            </button>
          </div>

          {/* Search & Filters */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
            <div style={{ position: 'relative', flex: 1, maxWidth: '300px' }}>
              <i className="fas fa-search" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}></i>
              <input type="text" placeholder="Search plans..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', width: '100%', outline: 'none' }} />
            </div>
            <button className="btn" style={{ border: '1px solid #d1d5db', padding: '10px 16px', borderRadius: '8px', color: '#6b7280', background: 'white', cursor: 'pointer' }}><i className="fas fa-filter me-2"></i>Filter</button>
            <button className="btn" style={{ border: '1px solid #d1d5db', padding: '10px 16px', borderRadius: '8px', color: '#F89423', background: '#fef3e2', cursor: 'pointer' }}><i className="fas fa-download me-2"></i>Export</button>
          </div>

          {/* Plans Table */}
          <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            {loading ? (
              <div className="text-center py-5"><div className="spinner-border text-primary" role="status"></div><p className="mt-3 text-muted">Loading plans...</p></div>
            ) : filteredPlans.length === 0 ? (
              <div className="text-center py-5 text-muted"><i className="fas fa-file-medical fa-3x mb-3 opacity-25"></i><h5>No plans found</h5><p>Click "Add Plan" to create your first product plan</p></div>
            ) : (
              <table className="table table-hover mb-0">
                <thead style={{ backgroundColor: '#f9fafb' }}>
                  <tr>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>S/N</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>Plan Name</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>Description</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>Premium (₦)</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>Coverage</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>Status</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPlans.map((plan, index) => (
                    <tr key={plan.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td className="py-3 px-4">{index + 1}</td>
                      <td className="py-3 px-4" style={{ fontWeight: '500' }}>{plan.name}</td>
                      <td className="py-3 px-4" style={{ fontSize: '13px', color: '#6b7280' }}>{plan.description}</td>
                      <td className="py-3 px-4" style={{ fontWeight: '600', color: '#F89423' }}>{plan.premium?.toLocaleString()}</td>
                      <td className="py-3 px-4" style={{ fontSize: '13px' }}>{plan.coverage}</td>
                      <td className="py-3 px-4">
                        <span className="badge" style={{ backgroundColor: plan.status === 'Active' ? '#d1fae5' : '#f3f4f6', color: plan.status === 'Active' ? '#10b981' : '#6b7280', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500', cursor: 'pointer' }} onClick={() => handleToggleStatus(plan.id, plan.status)}>
                          {plan.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="btn-group btn-group-sm">
                          <button className="btn btn-outline-primary btn-sm" onClick={() => handleOpenModal(plan)} title="Edit" style={{ border: '1px solid #3b82f6', color: '#3b82f6', background: 'white', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer' }}><i className="fas fa-edit"></i></button>
                          <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(plan.id, plan.name)} title="Delete" style={{ border: '1px solid #ef4444', color: '#ef4444', background: 'white', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', marginLeft: '4px' }}><i className="fas fa-trash"></i></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '12px', width: '90%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header" style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h5 style={{ margin: 0, color: '#1f2937' }}>{editingPlan ? 'Edit Plan' : 'Add New Plan'}</h5>
              <button onClick={handleCloseModal} style={{ background: 'none', border: 'none', fontSize: '24px', color: '#6b7280', cursor: 'pointer' }}>×</button>
            </div>
            <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Plan Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} required style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Description</label>
                <textarea name="description" value={formData.description} onChange={handleChange} rows="3" style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', resize: 'vertical' }} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Premium (₦) <span style={{ color: '#ef4444' }}>*</span></label>
                <input type="number" name="premium" value={formData.premium} onChange={handleChange} required min="0" style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Coverage Details</label>
                <input type="text" name="coverage" value={formData.coverage} onChange={handleChange} placeholder="e.g., Outpatient, Inpatient, Dental" style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
              </div>
              <div style={{ marginBottom: '24px' }}>
                <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Status</label>
                <select name="status" value={formData.status} onChange={handleChange} style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button type="button" onClick={handleCloseModal} style={{ padding: '10px 20px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', color: '#374151', cursor: 'pointer', fontWeight: '500' }}>Cancel</button>
                <button type="submit" style={{ padding: '10px 20px', background: '#F89423', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontWeight: '600' }}>{editingPlan ? 'Update Plan' : 'Create Plan'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CSS */}
      <style>{`
        .admin-page { display: flex; min-height: 100vh; background-color: #f8f9fa; }
        .sidebar { width: 260px; background: white; position: fixed; left: 0; top: 0; height: 100vh; overflow-y: auto; transition: transform 0.3s ease; z-index: 1000; border-right: 1px solid #e5e7eb; }
        .sidebar-header { padding: 20px; border-bottom: 1px solid #e5e7eb; display: flex; align-items: center; }
        .brand-logo { color: #333; font-weight: 700; text-decoration: none; display: flex; align-items: center; }
        .sidebar-nav { padding: 20px 0; }
        .nav-section { margin-bottom: 25px; }
        .nav-section-title { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: #F89423; padding: 0 20px; margin-bottom: 10px; }
        .nav-list { list-style: none; padding: 0; margin: 0; }
        .nav-link { display: flex; align-items: center; padding: 12px 20px; color: #333; text-decoration: none; transition: all 0.3s ease; font-weight: 500; }
        .nav-link:hover, .nav-link.active { background-color: #F89423; color: white; border-left: 4px solid white; }
        .sidebar-footer { margin-top: auto; padding-bottom: 20px; border-top: 1px solid #e5e7eb; padding-top: 20px; }
        .main-content { flex: 1; margin-left: 260px; display: flex; flex-direction: column; }
        .top-header { background: white; padding: 15px 30px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); display: flex; justify-content: space-between; align-items: center; position: sticky; top: 0; z-index: 100; }
        .header-left { display: flex; align-items: center; gap: 20px; }
        .header-right { display: flex; align-items: center; gap: 15px; }
        .header-icon { width: 40px; height: 40px; border-radius: 50%; border: none; background: #f3f4f6; color: #6b7280; cursor: pointer; display: flex; align-items: center; justify-content: center; }
        .header-icon:hover { background: #e5e7eb; color: #F89423; }
        .header-icon .badge { position: absolute; top: 5px; right: 5px; background: #F89423; color: white; font-size: 10px; padding: 2px 5px; border-radius: 10px; }
        .user-avatar { width: 40px; height: 40px; border-radius: 50%; background: linear-gradient(135deg, #F89423 0%, #F89423 100%); color: white; display: flex; align-items: center; justify-content: center; font-weight: 600; cursor: pointer; font-size: 14px; }
        .table th { background-color: #f9fafb; border-bottom: 1px solid #e5e7eb; font-weight: 600; font-size: 13px; color: #374151; }
        .table td { vertical-align: middle; border-bottom: 1px solid #f3f4f6; padding: 12px 16px; }
        .modal-overlay { animation: fadeIn 0.3s ease; }
        .modal-panel { animation: slideIn 0.3s ease; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideIn { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @media (max-width: 992px) { .sidebar { transform: translateX(-100%); } .sidebar.sidebar-open { transform: translateX(0); } .main-content { margin-left: 0; } }
      `}</style>
    </div>
  );
}

export default ProductPlans;